import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Container } from './primitives';
import { Logo } from './Logo';
import { FUNZIONALITA } from '@/content/funzionalita';

const NEWSLETTER_RECIPIENTS = ['d.marramiero@bossai.build', 'm.angelozzi@bossai.build'];

function buildNewsletterMailto(email: string): string {
  const subject = 'Nuova iscrizione newsletter';
  const body = `Nuova richiesta di iscrizione alla newsletter da: ${email}`;
  return `mailto:${NEWSLETTER_RECIPIENTS.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const COLS = [
  {
    title: 'Prodotto',
    links: [
      { label: 'Panoramica', href: '/prodotto' },
      ...FUNZIONALITA.map((f) => ({ label: f.name, href: `/funzionalita/${f.slug}` })),
    ],
  },
  {
    title: 'Azienda',
    links: [
      { label: 'Chi siamo', href: '/chi-siamo' },
      { label: 'Manifesto', href: '/manifesto' },
      { label: 'Clienti', href: '/clienti' },
      { label: 'Team', href: '/team' },
      { label: 'Candidatura spontanea', href: '/candidature' },
    ],
  },
  {
    title: 'Risorse',
    links: [
      { label: 'Playbook pilota', href: '#playbook' },
      { label: 'Sicurezza e dati', href: '#security' },
      { label: 'Documentazione', href: '#docs' },
      { label: 'Changelog', href: '#changelog' },
      { label: 'Status', href: '#status' },
    ],
  },
  {
    title: 'Legale',
    links: [
      { label: 'Privacy', href: '#privacy' },
      { label: 'Termini', href: '#terms' },
      { label: 'DPA', href: '#dpa' },
      { label: 'Cookie policy', href: '#cookies' },
    ],
  },
];

export function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState('');

  function handleNewsletterSubmit(e: FormEvent) {
    e.preventDefault();
    window.location.href = buildNewsletterMailto(newsletterEmail);
  }

  return (
    <footer className="relative border-t border-border pt-20 pb-10">
      <Container>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.4fr_2fr] lg:gap-20">
          <div>
            <Logo size={28} />
            <p className="mt-5 max-w-sm text-pretty text-[14px] leading-[1.55] text-fg-2">
              BOSS S.r.l. — Il gestionale nativo-AI della commessa edile. Costruito in Abruzzo, con il cemento
              sugli scarponi.
            </p>

            <form
              onSubmit={handleNewsletterSubmit}
              className="mt-7 flex max-w-sm items-center gap-2 rounded-full border border-input bg-card p-1"
            >
              <input
                required
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="tua@cantiere.it"
                className="min-w-0 flex-1 bg-transparent px-3 py-1.5 text-[13px] placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                type="submit"
                className="rounded-full bg-foreground px-4 py-1.5 text-[12px] font-medium text-background transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                Iscriviti →
              </button>
            </form>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-fg-disabled">
              Trimestrale · 4 numeri all'anno · zero fuffa
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {c.title}
                </div>
                <ul className="mt-4 space-y-2.5">
                  {c.links.map((l) => {
                    const isInternal = l.href.startsWith('/');
                    const cls =
                      'text-[13px] text-fg-2 transition-colors hover:text-foreground';
                    return (
                      <li key={l.label}>
                        {isInternal ? (
                          <Link to={l.href} className={cls}>
                            {l.label}
                          </Link>
                        ) : (
                          <a href={l.href} className={cls}>
                            {l.label}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-24 overflow-hidden">
          <div
            aria-hidden
            className="select-none text-[clamp(120px,28vw,440px)] font-light leading-[0.85] tracking-[-0.04em]"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in oklch, var(--foreground) 22%, transparent) 0%, transparent 90%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            BOSS<span className="text-primary">.</span>
          </div>
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background"
          />
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-fg-disabled md:flex-row md:items-center md:justify-between">
          <div>© 2026 · BOSS S.r.l. · Via Carducci 83, Pescara (PE) · P.IVA 02460870682</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a
              href="https://www.linkedin.com/company/113139110/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
            >
              ↗ LinkedIn
            </a>
            <span>↗ Dati · in UE</span>
            <span>↗ Fatto con espresso</span>
          </div>
        </div>
      </Container>
    </footer>
  );
}
